import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchAccountsByAccountIds } from "@/api/fetchAccountsByAccountIds/fetchAccountsByAccountIdsApi";
import type {
  CaraAccount,
  LeadGenPaymentSourceTypeSource,
} from "@/api/fetchAccountsByAccountIds/types";

const STOCHASTIC_CORPORATION = "stochastic";

export type BillabilityStatus = "billable" | "no_account" | "no_payment_method";

export type BillabilityResult = {
  status: BillabilityStatus;
  source: LeadGenPaymentSourceTypeSource;
};

interface AccountBillabilityState {
  billability: Partial<Record<string, BillabilityResult>>;
  loading: boolean;
  error: string | null;
}

const initialState: AccountBillabilityState = {
  billability: {},
  loading: false,
  error: null,
};

/**
 * Pure derivation of billability for a single account.
 * - null account → no_account
 * - account with null effectiveLeadGenPaymentSourceType → no_payment_method
 * - otherwise → billable, with the resolver-supplied source
 *
 * The source field is only meaningful when status === 'billable'.
 */
export const computeBillability = (
  account: CaraAccount | null,
): BillabilityResult => {
  if (account == null) {
    return { status: "no_account", source: null };
  }
  if (account.effectiveLeadGenPaymentSourceType == null) {
    return { status: "no_payment_method", source: null };
  }
  return {
    status: "billable",
    source: account.leadGenPaymentSourceTypeSource,
  };
};

const accountBillabilitySlice = createSlice({
  name: "accountBillability",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setBillability: (
      state,
      action: PayloadAction<Partial<Record<string, BillabilityResult>>>,
    ) => {
      Object.assign(state.billability, action.payload);
    },
  },
});

export const { setLoading, setError, setBillability } =
  accountBillabilitySlice.actions;

export const fetchAccountBillabilityAction =
  (accountIds: string[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    if (accountIds.length === 0) return;

    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const response = await fetchAccountsByAccountIds(
        STOCHASTIC_CORPORATION.toLowerCase(),
        accountIds,
      );
      const billability: Partial<Record<string, BillabilityResult>> = {};

      for (const [id, account] of Object.entries(response.data)) {
        billability[id] = computeBillability(account);
      }

      dispatch(setBillability(billability));
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to fetch account billability",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export default accountBillabilitySlice.reducer;
